import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../core/environments/environment';

import {
  CreateUser,
  EnableDisableUserResponse,
  GetUsersHttpParams,
  ListUsersResponse,
  ResCreateUser,
  ResDeleteUser,
  UpdateUser,
  UserData,
  UserSesionData,
  endpoint,
  initialSesionUser,
  initialUser,
} from '../models/users.model';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = environment.apiUrl;
  private readonly _cookieService = inject(CookieService);

  public currentUser = signal<UserSesionData>(initialSesionUser);
  public userRoutes = signal<string>('');

  constructor() {
    this.loadUserFromCookies();
  }

  private loadUserFromCookies() {
    const selectedUserFromCookies = this._cookieService.get('selectUser');
    if (selectedUserFromCookies) {
      const user = JSON.parse(selectedUserFromCookies);
      this.selectUser.set(user);
    }
  }

  public setCurrentUser(tokenInfo: UserSesionData) {
    const {
      id,
      name,
      last_name,
      email,
      user_role_id,
      role_value,
      role_name,
      institutions,
    } = tokenInfo;

    this.currentUser.set({
      id: id,
      email,
      user_role_id,
      name,
      last_name,
      role_name,
      role_value,
      institutions,
    });

    this.userRoutes.set(this.detectUserRoute(user_role_id));
  }
  public detectUserRoute(role_id: number): string {
    switch (role_id) {
      case 1:
        return 'admin';
      case 2:
        return 'rh';
      case 3:
        return 'declarant';
      case 4:
        return 'reviewer';
      default:
        return '';
    }
  }

  public selectUser = signal<UserData>(initialUser);
  public setSelectUser(user: UserData) {
    console.log("user", user);
    // TODO:Request information about this redundant process
    const {
      user_id,
      user_name,
      user_lastname,
      user_email,
      user_identification,
      user_role_id,
      user_last_connection_date,
      role_value,
      role_view_value_es,
      user_is_deleted,
      user_is_disabled,
      row_count,
      institutions,
      user_language_id,
    } = user;

    this.selectUser.set({
      user_id,
      user_name,
      user_lastname,
      user_email,
      user_identification,
      user_role_id,
      user_last_connection_date,
      role_value,
      role_view_value_es,
      user_is_deleted,
      user_is_disabled,
      row_count,
      institutions,
      user_language_id,
    });
    this.selectUser.set(user);
    this._cookieService.set('selectUser', JSON.stringify(user));
  }

  public getUsers(httpParams?: GetUsersHttpParams) {
    let params = new HttpParams({
      fromObject: {
        ...httpParams,
      },
    });
    if (httpParams?.institution_id && httpParams.institution_id.length > 0) {
      params = params.set(
        'institution_id',
        httpParams.institution_id.join(','),
      );
    }
    return this._http.get<ListUsersResponse>(
      `${this._apiUrl}/${endpoint.users}/${endpoint.listUsers}`,
      { params },
    );
  }

  public createUser(requestBody: CreateUser) {
    return this._http.post<ResCreateUser>(
      `${this._apiUrl}/${endpoint.users}/${endpoint.createUser}`,
      requestBody,
    );
  }

  public deleteUser(user_id: number) {
    return this._http.delete<ResDeleteUser>(
      `${this._apiUrl}/${endpoint.users}/${endpoint.deleteUser}/${user_id}`,
    );
  }

  public disableUser(user_id: number) {
    return this._http.put<EnableDisableUserResponse>(
      `${this._apiUrl}/${endpoint.users}/${endpoint.disableUser}/${user_id}`,
      null,
    );
  }

  public enableUser(user_id: number) {
    return this._http.put<EnableDisableUserResponse>(
      `${this._apiUrl}/${endpoint.users}/${endpoint.enableUser}/${user_id}`,
      null,
    );
  }

  public updateUser(requestBody: UpdateUser, userId: number | undefined) {
    return this._http.put<ResCreateUser>(
      `${this._apiUrl}/${endpoint.users}/${endpoint.updateUser}/${userId}`,
      requestBody,
    );
  }

  public getCurrentUserRole(): string {
    const user = this.currentUser();
    return user ? user.user_role_id.toString() : '';
  }

  public downloadAddUsersTemplate(): Observable<Blob> {
    const headers = new HttpHeaders({
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    return this._http.get(
      `${this._apiUrl}/${endpoint.dowloadCreateUserFormat}`,
      {
        headers,
        responseType: 'blob',
      },
    );
  }

  public downloadDisableUsersTemplate(): Observable<Blob> {
    const headers = new HttpHeaders({
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    return this._http.get(`${this._apiUrl}/${endpoint.dowloadDisableFormat}`, {
      headers,
      responseType: 'blob',
    });
  }

  public uploadCreateMassive(file: File): Observable<Blob> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this._http.post(
      `${this._apiUrl}/${endpoint.uploadCreateUser}`,
      formData,
      {
        headers: new HttpHeaders({
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        responseType: 'blob',
      },
    );
  }

  public uploadDisableMassive(file: File): Observable<Blob> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this._http.post(
      `${this._apiUrl}/${endpoint.uploadDisableUser}`,

      formData,
      {
        headers: new HttpHeaders({
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        responseType: 'blob',
      },
    );
  }
}
